import { z } from 'zod';

import { mongoIdSchema } from './shared';

export enum BrandProductCategory {
  Seller = 'seller',
  Reseller = 'reseller',
  Service = 'service',
  NA = '',
}

export enum BrandTypeOfBusiness {
  B2B = 'b2b',
  B2C = 'b2c',
  NA = '',
}

export enum BrandGender {
  All = 'all',
  Men = 'men',
  Women = 'women',
}

export enum ExcerptPlatform {
  Website = 'website',
  SocialMedia = 'socialMedia',
  Email = 'email',
  Advertising = 'advertising',
  Other = 'other',
}

export enum ExcerptSubPlatformWebsite {
  Homepage = 'homepage',
  ProductPage = 'productPage',
  BlogPost = 'blogPost',
  LandingPage = 'landingPage',
  AboutUsPage = 'aboutUsPage',
  CollectionPage = 'collectionPage',
  CaseStudy = 'caseStudy',
}

export enum ExcerptSubPlatformSocialMedia {
  LinkedIn = 'linkedin',
  Twitter = 'twitter',
  Facebook = 'facebook',
  Instagram = 'instagram',
  Pinterest = 'pinterest',
  Reddit = 'reddit',
}

export enum ExcerptSubPlatformEmail {
  Newsletter = 'newsletter',
  ColdOutreach = 'coldOutreach',
  ProductUpdate = 'productUpdate',
}

export enum ExcerptSubPlatformAdvertising {
  SearchAds = 'searchAds',
  SocialMediaAds = 'socialMediaAds',
  PrintAds = 'printAds',
}

export enum BrandVoice {
  FirstPersonSingular = 'firstPersonSingular',
  FirstPersonPlural = 'firstPersonPlural',
  SecondPerson = 'secondPerson',
  ThirdPerson = 'thirdPerson',
}

export type ExcerptSubPlatformType =
  | ExcerptSubPlatformWebsite
  | ExcerptSubPlatformSocialMedia
  | ExcerptSubPlatformEmail
  | ExcerptSubPlatformAdvertising
  | string;

export const brandPersonaSchema = z.object({
  label: z.string(),
  description: z.string().max(1000),
  age: z.string(),
  gender: z.enum([BrandGender.All, BrandGender.Men, BrandGender.Women]),
  interest: z.string().max(1000),
  painPoint: z.string().max(1000),
  isPrimaryPersona: z.boolean(),
});

export const brandExcerptSchema = z.object({
  platform: z.nativeEnum(ExcerptPlatform).default(ExcerptPlatform.Other),
  subPlatform: z
    .union([
      z.nativeEnum(ExcerptSubPlatformWebsite),
      z.nativeEnum(ExcerptSubPlatformSocialMedia),
      z.nativeEnum(ExcerptSubPlatformEmail),
      z.nativeEnum(ExcerptSubPlatformAdvertising),
      z.string(),
    ])
    .default(''),
  description: z.string().max(2000),
});

export type BrandExcerpt = z.infer<typeof brandExcerptSchema>;
export type BrandPersona = z.infer<typeof brandPersonaSchema>;

export const brandCreateSchema = z.object({
  name: z.string().min(2, 'BRAND_INVALID_NAME'),
  organizationId: mongoIdSchema,
  isDefault: z.boolean(),

  companyName: z.string(),
  companyWebsite: z.string(),
  productCategory: z.enum([
    BrandProductCategory.Seller,
    BrandProductCategory.Reseller,
    BrandProductCategory.Service,
    BrandProductCategory.NA,
  ]),
  productType: z.string(),
  typeOfBusiness: z.enum([
    BrandTypeOfBusiness.B2B,
    BrandTypeOfBusiness.B2C,
    BrandTypeOfBusiness.NA,
  ]),
  activity: z.string().max(2000),
  // brandValue: z.array(z.string()),
  persona: z.array(brandPersonaSchema).max(3),
  excerpt: z.array(brandExcerptSchema).max(10),

  extra: z.string().max(1000),

  country: z
    .string()
    .regex(/^[A-Za-z]{2}$/, 'BRAND_INVALID_COUNTRY')
    .or(z.literal('')),
  // TODO: should have a better rule for language
  language: z.string(),

  contentVoice: z.enum([
    BrandVoice.FirstPersonSingular,
    BrandVoice.FirstPersonPlural,
    BrandVoice.SecondPerson,
    BrandVoice.ThirdPerson,
  ]),
  contentVoiceTone: z.array(z.string()),
  contentInclusiveLanguage: z.boolean(),
  contentTalkAboutBrand: z.boolean(),
  contentHasEmoji: z.boolean(),
  productVoice: z.enum([
    BrandVoice.FirstPersonSingular,
    BrandVoice.FirstPersonPlural,
    BrandVoice.SecondPerson,
    BrandVoice.ThirdPerson,
  ]),
  productVoiceTone: z.array(z.string()),
  productInclusiveLanguage: z.boolean(),
  productTalkAboutBrand: z.boolean(),
  collectionVoice: z.enum([
    BrandVoice.FirstPersonSingular,
    BrandVoice.FirstPersonPlural,
    BrandVoice.SecondPerson,
    BrandVoice.ThirdPerson,
  ]),
  collectionVoiceTone: z.array(z.string()),
  collectionInclusiveLanguage: z.boolean(),
  collectionTalkAboutBrand: z.boolean(),

  blacklist: z.array(z.string()),
});
export type BrandCreate = z.infer<typeof brandCreateSchema>;

export const brandSchema = z
  .object({
    _id: mongoIdSchema,
    createdAt: z.number(),
    updatedAt: z.number(),
  })
  .merge(brandCreateSchema);

export type Brand = z.infer<typeof brandSchema>;
